import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getGeneratedQR, QRLogin } from "../services/QRSessionService";
import io from "socket.io-client";
import { loginConfirmedAction } from "../store/actions/AuthActions";
import {
  runLogoutTimer,
  saveTokenInLocalStorage,
} from "../services/AuthService";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";

const socket = io(process.env.REACT_APP_API_SOCKET_URL, {
  transports: ["polling"],
  withCredentials: false,
});

const LoginQRDialog = ({ open, handleClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  // ✅ Use useQuery for QR fetching (deduplicated and cached)
  const { data: qrResponse, isLoading: loading, refetch: fetchQR } = useQuery({
    queryKey: ["loginQR"],
    queryFn: async () => {
      const res = await getGeneratedQR();
      if (res.data) {
        socket.emit("register_qr", { token: res.data.token });
      }
      return res.data;
    },
    enabled: open,
    staleTime: 0, // QR codes are temporary
  });

  const qrData = qrResponse;

  // ✅ Listen for `qr_expired` socket event
  useEffect(() => {
    if (!open) return;

    const handleQRExpired = () => {
      fetchQR();
    };

    socket.on("qr_expired", handleQRExpired);
    return () => socket.off("qr_expired", handleQRExpired);
  }, [open, fetchQR]);

  // ✅ Listen for `qr_scanned` socket event
  useEffect(() => {
    if (!open) return;

    const handleQRScanned = async ({ token }) => {
      try {
        const response = await QRLogin({ token });
        if (response.data?.success) {
          const userData = response.data?.user;
          const jwt = response.data?.token;

          const data = {
            idToken: jwt,
            localId: userData?.id || "",
            name: userData?.name || "",
            email: userData?.email || "",
            refreshToken: jwt,
            expireDate: new Date(new Date().getTime() + 3600 * 1000),
            expiresIn: 3600,
          };

          await saveTokenInLocalStorage(data);
          await runLogoutTimer(dispatch, 3600 * 1000, navigate);
          await dispatch(loginConfirmedAction(data));
          handleClose();
        } else {
          setError("QR login failed. Please try again.");
        }
      } catch (err) {
        console.error("QR login error:", err);
        setError("Failed to complete login. Please try again.");
      }
    };

    socket.on("qr_scanned", handleQRScanned);
    return () => socket.off("qr_scanned", handleQRScanned);
  }, [open, dispatch, navigate, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#FFF4E6",
          borderRadius: "20px",
          overflow: "visible",
          padding: { xs: "15px 15px 30px 15px", sm: "30px 30px 60px 30px" },
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: { xs: 20, sm: 25 }, lineHeight: "100%" }}>
        Login With Your Glapsol Account
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: { xs: 10, sm: 42 }, right: { xs: 10, sm: 60 }, width: 30, height: 30, border: "1px solid #FF9500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, bgcolor: "#002045", p: { xs: "12px 20px", sm: "25px" }, borderRadius: "20px", alignItems: "center", gap: 4, flexWrap: "wrap", textAlign: { xs: "center", sm: "left" } }}>
          <Box sx={{ flex: 1, color: "#fff" }}>
            <Typography mb={2} sx={{ fontSize: { xs: 20, sm: 30 }, lineHeight: "100%" }} fontWeight={700}>Step To Scan QR Code</Typography>
            <Typography mb={1} sx={{ fontSize: { xs: 14, sm: 16 } }}>1. Open <strong>Glapsol App</strong></Typography>
            <Typography mb={1} sx={{ fontSize: { xs: 14, sm: 16 } }}>2. Tap Scan Option Available At The Bottom</Typography>
            <Typography sx={{ fontSize: { xs: 14, sm: 16 } }}>3. Point Glapsol Scan At QR Code To Login</Typography>
          </Box>

          <Box sx={{ background: "#fff", padding: 1, width: { xs: 120, sm: 169 }, height: { xs: 120, sm: 169 }, borderRadius: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
            {loading ? (
              <CircularProgress size={40} color="warning" />
            ) : error ? (
              <Typography color="error" fontSize={12} textAlign="center">{error}</Typography>
            ) : qrData?.qrImage ? (
              <img src={qrData.qrImage} alt="QR Code" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }} />
            ) : (
              <Typography fontSize={12} color="text.secondary">No QR Code</Typography>
            )}
          </Box>
        </Box>

        <Typography fontSize={{ xs: 12, sm: 15 }} pt={1} pb={1} textAlign={'center'} color="#000000">
          By Signing In You Agree To Our <a href="https://api.glapsol.com/uploads/cmspages/privacypolicy.html" target="_blank" rel="noopener noreferrer" style={{ color: "#FF9500", fontWeight: 700, textDecoration: "none" }}>Privacy Policy</a> And <a href="https://api.glapsol.com/uploads/cmspages/termscondition.html" target="_blank" rel="noopener noreferrer" style={{ color: "#FF9500", fontWeight: 700, textDecoration: "none" }}>Terms Of Use</a>
        </Typography>

        <Divider sx={{ mb: 2, color: "#CCCCCC" }} />

        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}>
          <Typography fontSize={{ xs: 12, sm: 15 }} fontWeight={400}>To Create An Account Download <br /><Box component="span" sx={{ color: "#FF9500", fontWeight: 700 }}>Glapsol App</Box></Typography>
          <Box sx={{ display: "flex", gap: 2, mt: { xs: 2, sm: 0 }, flexDirection: { xs: "column", sm: "row" } }}>
            <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83" alt="App Store" style={{ height: 40 }} />
            <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" style={{ height: 40, borderRadius: "8px" }} />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoginQRDialog;
